import React from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layout,
  Table,
  Button,
  Upload,
  Modal,
  message,
  Row,
  Col,
  Tooltip,
  Input,
  InputNumber,
  Space,
  Typography,
} from "antd";
import {
  PlusCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
  DeleteOutlined,
  DollarCircleOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { Header, AutoCompleteCompany, InputCurrency } from "components";
import { CardInfo } from "components/card/card-info";
import { formatMoney } from "utils";
import { read, utils, writeFile } from "xlsx";
import { createRewardBatch } from "services/reward-batch-service";
import { createBatchInCampaign } from "services/reward-campaign-service";
import { getTotalBalance } from "services/company-service";

const { Dragger } = Upload;
const { Footer, Content } = Layout;

const phoneDigits = (v) => v.replace(/\D/g, "");

const formatPhoneMask = (value) => {
  const d = phoneDigits(value).slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const isValidPhone = (phone) => {
  const len = phoneDigits(phone).length;
  return len === 10 || len === 11;
};

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const RewardVoucherScreen = (props) => {
  const { login } = props;
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [company, setCompany] = React.useState(login.company.id);
  const [companyBalance, setCompanyBalance] = React.useState(0);
  const [importData, setImportData] = React.useState([]);
  const [importSearch, setImportSearch] = React.useState("");
  const [selectedImportKeys, setSelectedImportKeys] = React.useState([]);
  const [loadingBalance, setLoadingBalance] = React.useState(false);
  const [loadingCreate, setLoadingCreate] = React.useState(false);
  const [idempotentKey, setIdempotentKey] = React.useState(() => crypto.randomUUID());
  const [batchName, setBatchName] = React.useState("");
  const [applyAllExpiration, setApplyAllExpiration] = React.useState({
    show: false,
    value: null,
  });
  const [applyAllValue, setApplyAllValue] = React.useState({
    show: false,
    value: null,
  });

  React.useEffect(() => {
    loadCompanyBalance();
  }, [company]);

  const loadCompanyBalance = async () => {
    try {
      if ((login.company.master && !company) || !company) {
        return;
      }
      setLoadingBalance(true);
      const response = await getTotalBalance(
        login.company.master ? company : null,
      );
      setCompanyBalance(response.balance);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const onUploadSheet = async (file) => {
    try {
      const fileBuffer = await file.arrayBuffer();
      const workBook = read(fileBuffer, { cellDates: true });
      const workSheet = workBook.Sheets[workBook.SheetNames[0]];
      const rows = utils.sheet_to_json(workSheet, { defval: "" });
      const parsed = rows
        .filter((r) => r["Valor"])
        .map((r, i) => ({
          key: i,
          email: r["Email"] || "",
          phone: formatPhoneMask(String(r["Telefone"] || "")),
          amount: parseFloat(String(r["Valor"]).replace(",", ".")) || 0,
          expirationDate: parseInt(r["Prazo (dias)"], 10) || 90,
        }));
      setImportData(parsed);
      setImportSearch("");
      setSelectedImportKeys([]);
    } catch (err) {
      message.error("Erro ao processar a planilha.");
    }
    return false;
  };

  const onEditValue = (key, newValue) => {
    setImportData((prev) =>
      prev.map((r) => (r.key === key ? { ...r, amount: newValue } : r)),
    );
  };

  const onEditExpiration = (key, newValue) => {
    setImportData((prev) =>
      prev.map((r) => (r.key === key ? { ...r, expirationDate: newValue } : r)),
    );
  };

  const onEditEmail = (key, newValue) => {
    setImportData((prev) =>
      prev.map((r) => (r.key === key ? { ...r, email: newValue } : r)),
    );
  };

  const onEditPhone = (key, rawValue) => {
    setImportData((prev) =>
      prev.map((r) => (r.key === key ? { ...r, phone: formatPhoneMask(rawValue) } : r)),
    );
  };

  const onConfirmApplyAllExpiration = () => {
    setImportData((prev) =>
      prev.map((r) => ({ ...r, expirationDate: applyAllExpiration.value })),
    );
    setApplyAllExpiration({ show: false, value: null });
  };

  const onConfirmApplyAllValue = () => {
    setImportData((prev) =>
      prev.map((r) => ({ ...r, amount: applyAllValue.value })),
    );
    setApplyAllValue({ show: false, value: null });
  };

  const onRemoveSelected = () => {
    setImportData((prev) =>
      prev.filter((r) => !selectedImportKeys.includes(r.key)),
    );
    setSelectedImportKeys([]);
  };

  const onCreateLinks = async () => {
    try {
      if (login.company.master) {
        message.warning("Master não pode criar links.");
        return;
      }

      if (!batchName || batchName.trim() === "") {
        message.warning("Informe o nome do lote antes de criar.");
        return;
      }

      if (importData.length === 0) {
        message.warning("Importe uma planilha antes de criar os links.");
        return;
      }
      if (importData.some((r) => !r.amount || r.amount <= 0)) {
        message.warning(
          "Todos os registros devem ter um valor maior que zero.",
        );
        return;
      }
      if (importData.some((r) => !r.expirationDate || r.expirationDate < 2)) {
        message.warning(
          "Todos os registros devem ter um prazo de expiração maior que 1 dia.",
        );
        return;
      }
      if (importData.some((r) => r.phone && !isValidPhone(r.phone))) {
        message.warning(
          "Corrija os telefones com formato inválido antes de continuar.",
        );
        return;
      }

      Modal.confirm({
        title: "Confirmar criação do lote",
        content: `Criar lote "${batchName.trim()}" com ${importData.length} voucher(s)?`,
        okText: "Criar Lote",
        cancelText: "Cancelar",
        onOk: async () => {
          try {
            setLoadingCreate(true);
            const body = importData.map((line) => ({
              email: line.email,
              phone: (line.phone || "").replace(/\D/g, ""),
              amount: line.amount,
              expirationDate: line.expirationDate,
            }));
            const data = campaignId
              ? await createBatchInCampaign(campaignId, batchName.trim(), body, idempotentKey)
              : await createRewardBatch(batchName.trim(), body, idempotentKey);
            setLoadingCreate(false);
            if (campaignId) {
              navigate(`/reward-voucher/${campaignId}`);
            } else if (data && data.batchId) {
              navigate(`/reward-voucher/${data.batchId}`);
            } else {
              navigate("/reward-voucher");
            }
          } catch (err) {
            console.log(err);
            setLoadingCreate(false);
            message.error("Erro ao criar lote. Tente novamente.");
          }
        },
      });
    } catch (err) {
      console.log(err);
      message.error("Erro ao criar lote. Tente novamente.");
    }
  };

  const onDownloadTemplate = () => {
    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet([
      ["Email", "Telefone", "Valor", "Prazo (dias)"],
    ]);
    ws["!cols"] = [{ wch: 30 }, { wch: 18 }, { wch: 12 }, { wch: 14 }];
    utils.book_append_sheet(wb, ws, "Template");
    writeFile(wb, "template_voucher_premiado.xlsx");
  };

  const totalImported = React.useMemo(
    () => importData.reduce((sum, l) => sum + (l.amount || 0), 0),
    [importData],
  );

  const filteredImportData = React.useMemo(() => {
    const q = importSearch.trim().toLowerCase();
    if (!q) return importData;
    return importData.filter((r) => {
      const valueStr = String(r.amount);
      return (
        r.email.toLowerCase().includes(q) ||
        String(r.phone).toLowerCase().includes(q) ||
        valueStr.includes(q)
      );
    });
  }, [importData, importSearch]);

  return (
    <Layout className="alymente-layout">
      <Header
        title="Link+"
        middle={
          login.company.master ? (
            <AutoCompleteCompany value={company} onChange={setCompany} />
          ) : undefined
        }
      />
      <Content style={{ padding: 16 }}>
        <Row style={{ marginBottom: 16, gap: 16 }}>
          <Col>
            <CardInfo
              style={{ minWidth: 300, minHeight: 115 }}
              loading={loadingBalance}
              title="Saldo Atual"
              value={formatMoney(companyBalance)}
            />
          </Col>
          {importData.length > 0 && (
            <Col>
              <CardInfo
                style={{ minWidth: 300, minHeight: 115 }}
                loading={false}
                title="Valor Total do Lote"
                value={formatMoney(totalImported)}
              />
            </Col>
          )}
        </Row>

        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
              Nome do Lote <Typography.Text type="danger">*</Typography.Text>
            </Typography.Text>
            <Input
              placeholder="Ex.: Premiação - Julho"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Col>
        </Row>

        {importData.length === 0 ? (
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row justify="end">
                <Col>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={onDownloadTemplate}
                  >
                    Baixar Template
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Dragger
                name="reward_voucher_file"
                multiple={false}
                showUploadList={false}
                accept=".xls,.xlsx,.ods"
                beforeUpload={onUploadSheet}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">
                  Clique aqui ou arraste sua planilha para fazer upload
                </p>
                <p className="ant-upload-hint">
                  Colunas esperadas:{" "}
                  <strong>Email, Telefone, Valor*, Prazo (dias)*</strong>
                </p>
              </Dragger>
            </Col>
          </Row>
        ) : (
          <>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 8 }}
            >
              <Col>
                {selectedImportKeys.length > 0 ? (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={onRemoveSelected}
                  >
                    Remover selecionados ({selectedImportKeys.length})
                  </Button>
                ) : (
                  <Upload
                    name="reward_voucher_file"
                    multiple={false}
                    showUploadList={false}
                    accept=".xls,.xlsx,.ods"
                    beforeUpload={onUploadSheet}
                  >
                    <Button icon={<UploadOutlined />}>Trocar planilha</Button>
                  </Upload>
                )}
              </Col>
              <Col>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Buscar por e-mail, telefone ou valor"
                  value={importSearch}
                  onChange={(e) => setImportSearch(e.target.value)}
                  allowClear
                  style={{ width: 320 }}
                />
              </Col>
            </Row>
            <Table
              dataSource={filteredImportData}
              rowKey="key"
              pagination={false}
              scroll={{ y: "40vh" }}
              bordered
              size="small"
              rowSelection={{
                selectedRowKeys: selectedImportKeys,
                onChange: setSelectedImportKeys,
              }}
            >
              <Table.Column
                title="E-mail"
                dataIndex="email"
                key="email"
                align="center"
                render={(value, record) => (
                  <Input
                    value={value}
                    placeholder="email@exemplo.com"
                    onChange={(e) => onEditEmail(record.key, e.target.value)}
                  />
                )}
              />
              <Table.Column
                title="Telefone"
                dataIndex="phone"
                key="phone"
                align="center"
                render={(value, record) => {
                  const hasError = value && !isValidPhone(value);
                  return (
                    <Tooltip title={hasError ? "Formato inválido. Use (00) 0000-0000 ou (00) 00000-0000" : undefined}>
                      <Input
                        value={value}
                        placeholder="(00) 00000-0000"
                        status={hasError ? "error" : undefined}
                        onChange={(e) => onEditPhone(record.key, e.target.value)}
                      />
                    </Tooltip>
                  );
                }}
              />
              <Table.Column
                title={() => (
                  <Space>
                    Valor
                    <Tooltip title="Aplicar valor para todos os itens">
                      <DollarCircleOutlined
                        style={{ cursor: "pointer", fontSize: 16 }}
                        onClick={() =>
                          setApplyAllValue({ show: true, value: null })
                        }
                      />
                    </Tooltip>
                  </Space>
                )}
                dataIndex="amount"
                key="amount"
                width={200}
                align="center"
                render={(value, record) => (
                  <InputCurrency
                    value={value}
                    onChange={(v) => onEditValue(record.key, v)}
                  />
                )}
              />
              <Table.Column
                title={() => (
                  <Space>
                    Prazo (dias)
                    <Tooltip title="Aplicar prazo para todos os itens">
                      <FieldTimeOutlined
                        style={{ cursor: "pointer", fontSize: 16 }}
                        onClick={() =>
                          setApplyAllExpiration({ show: true, value: null })
                        }
                      />
                    </Tooltip>
                  </Space>
                )}
                dataIndex="expirationDate"
                key="expirationDate"
                width={170}
                align="center"
                render={(value, record) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    placeholder="Ex: 30"
                    value={value}
                    onChange={(v) => onEditExpiration(record.key, v)}
                    style={{ width: "100%" }}
                  />
                )}
              />
            </Table>
          </>
        )}
      </Content>
      <Footer style={{ display: "flex", justifyContent: "space-between" }}>
        <Button type="primary" onClick={() => navigate(campaignId ? `/reward-voucher/${campaignId}` : "/reward-voucher")}>
          Voltar
        </Button>
        <Button
          className="btn-save"
          type="primary"
          icon={<PlusCircleOutlined />}
          loading={loadingCreate}
          disabled={importData.length === 0}
          onClick={onCreateLinks}
        >
          Criar Lote
        </Button>
      </Footer>
      <Modal
        width={400}
        title="Aplicar valor para todos os itens"
        open={applyAllValue.show}
        okText="Aplicar"
        cancelText="Cancelar"
        onOk={onConfirmApplyAllValue}
        onCancel={() => setApplyAllValue({ show: false, value: null })}
        okButtonProps={{
          disabled: !applyAllValue.value || applyAllValue.value <= 0,
        }}
      >
        <InputCurrency
          value={applyAllValue.value}
          onChange={(v) => setApplyAllValue((prev) => ({ ...prev, value: v }))}
        />
      </Modal>
      <Modal
        width={400}
        title="Aplicar prazo para todos os itens"
        open={applyAllExpiration.show}
        okText="Aplicar"
        cancelText="Cancelar"
        onOk={onConfirmApplyAllExpiration}
        onCancel={() => setApplyAllExpiration({ show: false, value: null })}
        okButtonProps={{
          disabled: !applyAllExpiration.value || applyAllExpiration.value < 2,
        }}
      >
        <InputNumber
          min={1}
          precision={0}
          placeholder="Ex: 30"
          value={applyAllExpiration.value}
          onChange={(v) =>
            setApplyAllExpiration((prev) => ({ ...prev, value: v }))
          }
          style={{ width: "100%" }}
        />
      </Modal>
    </Layout>
  );
};

export default connect(mapStateToProps, {})(RewardVoucherScreen);
